import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { Button, Card, type CardProps, Box, Flex, Text } from '@mantine/core'

import {
  useQueryGetAllBookingProducts,
  useQueryGetBookingById,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreCustomer, type ParameterID } from '@flecto/types'
import { Icon } from '@flecto/ui'

import { useBookingLoadingState } from '../../../booking-details'
import { useModalBookingCustomerSelection } from '../../hooks/useModalBookingCustomerSelection'
import { TableBookingCustomer } from '../TableBookingCustomer/TableBookingCustomer'

export type CardBookingCustomerSelectionProps = Omit<CardProps, 'children'> & {
  bookingId?: ParameterID
}

export function CardBookingCustomerSelection({
  bookingId,
  ...props
}: CardBookingCustomerSelectionProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_customer' })

  const { data: booking, isLoading: isLoadingBooking } = useQueryGetBookingById(
    { bookingId }
  )

  const {
    isLoading: isLoadingBookingProducts,
    isRefetching: isRefetchingBookingProducts,
  } = useQueryGetAllBookingProducts({ bookingId })

  const { isUpdating: isUpdatingBooking } = useBookingLoadingState()

  const { openModal } = useModalBookingCustomerSelection({ booking })

  return (
    <Card {...props} withBorder>
      <Flex align='center' justify='space-between'>
        <Box style={{ flex: 1 }}>
          <Text fw={500}>{t('label_customer')}</Text>
          <Text c='dimmed' size='sm'>
            {t('card_customer_selection_description')}
          </Text>
        </Box>
      </Flex>
      {Boolean(booking?.customer) && (
        <TableBookingCustomer
          data-testid='customer-table'
          mt='md'
          booking={booking}
          customer={booking?.customer as CoreCustomer}
          isLoading={isLoadingBooking}
          withBorder={false}
        />
      )}
      {!booking?.customer && (
        <Button
          data-testid='customer-add-button'
          mt='md'
          variant='outline'
          leftSection={<Icon icon={faPlus} />}
          onClick={openModal}
          loading={
            isUpdatingBooking ||
            isLoadingBookingProducts ||
            isRefetchingBookingProducts
          }
        >
          {t('label_select_customer')}
        </Button>
      )}
    </Card>
  )
}
